import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { http } from '@/api/http';
import { disconnectSocket } from '@/api/socket';
import type {
  LoginRequestDto,
  LoginResponseDto,
  LoginRequiresSetupDto,
  UserDto,
} from '@guard/shared';

const STORAGE_KEY = 'guard.auth.token';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(STORAGE_KEY));
  const user = ref<UserDto | null>(null);
  const loading = ref(false);
  const requiresSetup = ref<LoginRequiresSetupDto | null>(null);

  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => !!user.value?.isAdmin);

  function setToken(value: string | null): void {
    token.value = value;
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  }

  async function login(dto: LoginRequestDto): Promise<LoginResponseDto> {
    loading.value = true;
    try {
      const { data } = await http.post<LoginResponseDto>('/auth/login', dto);
      if ('requiresSetup' in data) {
        requiresSetup.value = data;
        return data;
      }
      requiresSetup.value = null;
      setToken(data.token);
      user.value = data.user;
      return data;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe(): Promise<UserDto | null> {
    if (!token.value) return null;
    try {
      const { data } = await http.get<UserDto>('/auth/me');
      user.value = data;
      return data;
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } }).response?.status;
      // Drop the token only if the server explicitly rejected it.
      // For network errors / 5xx — keep the token so the user isn't kicked
      // out by a flaky network or a backend restart blip.
      if (status === 401) {
        setToken(null);
        user.value = null;
      }
      return null;
    }
  }

  async function logout(): Promise<void> {
    if (token.value) {
      try {
        await http.post('/auth/logout');
      } catch {
        /* ignore */
      }
    }
    setToken(null);
    user.value = null;
    requiresSetup.value = null;
    disconnectSocket();
  }

  return {
    token,
    user,
    loading,
    requiresSetup,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    fetchMe,
    setToken,
  };
});
